import React, { useState, useMemo, useEffect, useRef } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Table from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { getApiInstance } from "../../../utils/api";
import { nombreFicheroDescarga } from "../../../utils/descargas";
import { mapList } from "../../../utils/utils";
import { useZone } from "../../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../../constants/zones";
import { getZoneLabel } from "../../../utils/zoneLabels";
import { useTranslation } from "react-i18next";
import { FaClock } from "react-icons/fa";
import { CiClock1 } from "react-icons/ci";

const estados = [
    { label: "", type: "info" },
    { label: "Pendiente", type: "warning", Icon: FaClock },
    { label: "Incidencia", type: "danger", Icon: CgDanger },
    { label: "Validada", type: "success", Icon: CiCircleCheck },
];

export default function Productores({ user }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const canAdd = Number(user.codAcceso) === 1 && Number(user.role) !== 4;
    const [productores, setProductores] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const { selectedZone } = useZone();

    const cifLabel = t("fields.cif");
    const emailLabel = t("fields.email");
    const representativeDniLabel = t("producers.form.representativeDni");
    const representativeDniPlaceholder = t("producers.form.representativeDniPlaceholder");
    const communityLabel = t("fields.community");
    const provinceLabel = t("fields.province");
    const localityLabel = t("fields.locality");
    const communityPlaceholder = t("placeholders.enterCommunity");
    const provincePlaceholder = t("placeholders.enterProvince");
    const localityPlaceholder = t("placeholders.enterLocality");

    const columnasProductores = [
        { key: "id", label: "codProductor", visible: false },
        { key: "codProductor", label: "codProductor", visible: false },
        {
            key: "nombre",
            label: t("fields.name"),
            visible: true,
            sortable: true,
            isLink: true,
        },
        {
            key: "cif",
            label: cifLabel,
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "contacto",
            label: t("fields.contact"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
            render: (row) => {
                return (
                    <div className="table-cell-content">
                        <span className="table-cell-content__primary">
                            {row.contacto || "-"}
                        </span>
                        <span className="table-cell-content__secondary">
                            {row.eMail || ""}
                        </span>
                    </div>
                );
            },
        },
        {
            key: "telefono",
            label: t("fields.phone"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "eMail",
            label: emailLabel,
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "codEstado",
            label: t("fields.validation"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
    ];

    useEffect(() => {
        fetchProductores();
        fetchComunidades();
        fetchPaises();
        fetchProvincias();
    }, []);

    const fetchProductores = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let productoresData = [];

            const response = await api.get(
                `/api/Productores/GetProductores`
            );
            productoresData = response.data;

            setProductores(productoresData);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los productores. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchComunidades = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            comunidadesData = response.data;

            setComunidades(
                mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                )
            );

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPaises = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchProvincias = async () => {
        setLoading(true);
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            provinciasData = response.data;

            setProvincias(
                mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterFields = [
        { key: "nombre", label: t("fields.name"), type: "multiselect" },
        {
            key: "codEstado",
            label: t("fields.state"),
            type: "multiselect",
            labelMap: [
                t("states.noData"),
                t("states.pending"),
                t("states.incident"),
                t("states.validated"),
            ],
        },
        { key: "codZona", label: "Zona", type: "multiselect", labelMap: ZONE_LABEL_MAP },
    ];

    const estados = [
        { type: "primary", label: t("states.noData") },
        { type: "info", label: t("states.pending"), Icon: CiClock1 },
        { type: "warning", label: t("states.incident"), Icon: CgDanger },
        { type: "success", label: t("states.validated"), Icon: CiCircleCheck },
    ];

    const [exportando, setExportando] = useState(false);

    const generateExcel = async ({ codsProductores }) => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();

        try {
            const response = await api.post(
                "/api/Productores/CreateReportByCods",
                codsProductores,
                {
                    responseType: "blob",
                }
            );

            const fileName = nombreFicheroDescarga(
                response.headers["content-disposition"],
                "Informe_Productores.xlsx"
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setExportando(false);
        }
    };

    const handleExportarSeleccionados = () => {
        let filasAExportar;

        if (selectedRows && selectedRows.size > 0) {
            // Si hay filas marcadas, exportar solo esas.
            filasAExportar = (productores ?? []).filter((p) =>
                [...selectedRows].some(
                    (id) => String(id) === String(p.codProductor ?? p.id)
                )
            );
        } else {
            // Si no hay seleccion, exportar las filas que muestra la tabla
            // (respetando los filtros aplicados, incluido el de Zona).
            filasAExportar = tableRef.current?.getVisibleRows() ?? [];
        }

        if (filasAExportar.length === 0) {
            setError(
                "No hay productores que exportar con los filtros aplicados."
            );
            return;
        }

        const codsProductores = filasAExportar.map(
            (item) => item.codProductor ?? item.id
        );
        generateExcel({ codsProductores });
    };

    return (
        <PageLayout
            title={t("producers.title")}
            subtitle={t("producers.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoMdAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${t("fields.producer")}`}
                          </Button>,
                      ]
                    : []),
                <Button
                    key="exportar"
                    variant="filled"
                    startIcon={FiDownload({})}
                    onClick={handleExportarSeleccionados}
                    endIcon={undefined}
                    disabled={exportando}
                >
                    {exportando ? "Generando..." : "Exportar"}
                </Button>,
            ]}
        >
            <Table
                ref={tableRef}
                hideAddButton={true}
                columns={columnasProductores}
                dataTable={productores}
                onDataUpdate={setProductores}
                showCheckboxes={true}
                elementName={t("fields.producer")}
                noResultsMessage={t("producers.noProducersFound")}
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}
                expandable={true}
                expandableBreakpoint={600}
                actions={["Eliminar", "Editar"]}
                searchTerms={["nombre", "cif", "telefono", "eMail", "codEstado"]}
                formElements={[
                    {
                        title: t("sections.generalInfo"),
                        titleVisible: true,
                        elements: [
                            {
                                name: "nombre",
                                type: "text",
                                label: t("fields.name"),
                                placeholder: t("placeholders.enterName"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cif",
                                type: "text",
                                label: cifLabel,
                                placeholder: t("placeholders.enterCif"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "contacto",
                                type: "text",
                                label: t("fields.contact"),
                                placeholder: t(
                                    "producers.form.contactNamePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "telefono",
                                type: "tel",
                                label: t("fields.phone"),
                                placeholder: t("placeholders.enterPhone"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "eMail",
                                type: "email",
                                label: emailLabel,
                                placeholder: t("placeholders.enterEmail"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                        ],
                    },
                    {
                        title: t("producers.form.representativeData"),
                        titleVisible: true,
                        elements: [
                            {
                                name: "nombreRepresentante",
                                type: "text",
                                label: t("producers.form.representativeName"),
                                placeholder: t(
                                    "producers.form.representativeNamePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "apellidosRepresentante",
                                type: "text",
                                label: t(
                                    "producers.form.representativeLastName"
                                ),
                                placeholder: t(
                                    "producers.form.representativeLastNamePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "dniRepresentante",
                                type: "text",
                                label: representativeDniLabel,
                                placeholder: representativeDniPlaceholder,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cargoRepresentante",
                                type: "text",
                                label: t(
                                    "producers.form.representativePosition"
                                ),
                                placeholder: t(
                                    "producers.form.representativePositionPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "telefonoContacto",
                                type: "tel",
                                label: t("producers.form.contactPhone"),
                                placeholder: t(
                                    "producers.form.contactPhonePlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "eMailContacto",
                                type: "email",
                                label: t("producers.form.contactEmail"),
                                placeholder: t(
                                    "producers.form.contactEmailPlaceholder"
                                ),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                    {
                        name: "cnae",
                        type: "number",
                        label: t("producers.form.cnae"),
                        placeholder: t(
                            "producers.form.cnaePlaceholder"
                        ),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                        ],
                    },
                    {
                        title: t("sections.location"),
                        titleVisible: true,
                        elements: [
                            {
                                name: "direccion",
                                type: "text",
                                label: t("fields.address"),
                                placeholder: t("placeholders.enterAddress"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codPais",
                                type: "select",
                                label: t("fields.country"),
                                placeholder: t("placeholders.enterCountry"),
                                options: paises,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codComunidad",
                                type: "select",
                                label: communityLabel,
                                placeholder: communityPlaceholder,
                                options: comunidades,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                associatedFormElement: "codPais",
                            },
                            {
                                name: "codProvincia",
                                type: "select",
                                label: provinceLabel,
                                placeholder: provincePlaceholder,
                                options: provincias,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                                associatedFormElement: "codComunidad",
                            },
                            {
                                name: "localidad",
                                type: "text",
                                label: localityLabel,
                                placeholder: localityPlaceholder,
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "cp",
                                type: "text",
                                label: t("fields.postalCode"),
                                placeholder: t("placeholders.enterPostalCode"),
                                required: true,
                                disabled: false,
                                labelVisible: true,
                                visible: true,
                            },
                            {
                                name: "codZona",
                                type: "number",
                                label: "Zona",
                                visible: false,
                                defaultValue: selectedZone.value,
                            },
                        ],
                    },
                ]}
                filterFields={filterFields}
                loading={loading}
                sortProp="nombre"
                addApiUrl="/api/Productores/SetProductor"
                editApiUrl="/api/Productores/UpdateProductor"
                deleteApiUrl="/api/Productores/DeleteProductor"
                keyProp="codProductor"
                stateProp="codEstado"
                states={estados}
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
            />
        </PageLayout>
    );
}
